import { composeArray } from '@strings';
import type { Angle } from './Angle';
import type { Point } from './Point';

/// Represents an immutable 3x3 matrix that can be used to transform points in 2D space.
export class Matrix {
  #m: Float32Array;

  /// Constructs a new matrix from the given 9-element Float32Array.
  constructor(elements: Float32Array) {
    if (elements.length !== 9) {
      throw new Error('Matrix must have 9 elements');
    }
    // Copy the elements to ensure immutability.
    this.#m = new Float32Array(elements);
  }

  /// Returns the matrix as a 9-element Float32Array.
  get elements(): Float32Array {
    // Return a copy to ensure immutability.
    return new Float32Array(this.#m);
  }

  /// Returns the identity matrix.
  static identity(): Matrix {
    return new Matrix(new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]));
  }

  /// Returns a matrix that translates by the given vector.
  static translation(v: Point): Matrix {
    return new Matrix(new Float32Array([1, 0, v.x, 0, 1, v.y, 0, 0, 1]));
  }

  /// Returns a matrix that scales by the given factor.
  static scaling(s: Point): Matrix {
    return new Matrix(new Float32Array([s.x, 0, 0, 0, s.y, 0, 0, 0, 1]));
  }

  /// Returns a matrix that rotates by the given angle.
  static rotation(angle: Angle): Matrix {
    const c = angle.cos;
    const s = angle.sin;
    return new Matrix(new Float32Array([c, -s, 0, s, c, 0, 0, 0, 1]));
  }

  /// Returns the product of the two matrices or array of matrices.
  product(other: Matrix | Matrix[]): Matrix {
    if (other instanceof Matrix) {
      const m = this.#m;
      const o = other.#m;
      return new Matrix(
        new Float32Array([
          m[0] * o[0] + m[1] * o[3] + m[2] * o[6],
          m[0] * o[1] + m[1] * o[4] + m[2] * o[7],
          m[0] * o[2] + m[1] * o[5] + m[2] * o[8],
          m[3] * o[0] + m[4] * o[3] + m[5] * o[6],
          m[3] * o[1] + m[4] * o[4] + m[5] * o[7],
          m[3] * o[2] + m[4] * o[5] + m[5] * o[8],
          m[6] * o[0] + m[7] * o[3] + m[8] * o[6],
          m[6] * o[1] + m[7] * o[4] + m[8] * o[7],
          m[6] * o[2] + m[7] * o[5] + m[8] * o[8]
        ])
      );
    } else {
      return other.reduce((acc, m) => acc.product(m), this);
    }
  }

  toString(fractionDigits?: number): string {
    return composeArray(Array.from(this.#m), fractionDigits);
  }
}
